import React from "react";
import { Helmet } from "react-helmet-async";
import { useT } from "@/lib/i18n";
import {
  SITE,
  HERO_OG,
  buildLodgingSchema,
  buildWebSiteSchema,
  buildNearbySchema,
  buildFaqSchema,
  buildBreadcrumbSchema,
} from "@/lib/seoSchemas";

export const SeoHead = () => {
  const { t, lang } = useT();
  const isEN = lang === "en";
  const canonical = isEN ? `${SITE}/en` : `${SITE}/`;
  const ogLocale = isEN ? "en_US" : "sk_SK";
  const ogLocaleAlt = isEN ? "sk_SK" : "en_US";

  const breadcrumbs = [{ name: isEN ? "Home" : "Úvod", item: canonical }];

  return (
    <Helmet>
      <html lang={lang} />
      <title>{t.seo.title}</title>
      <meta name="description" content={t.seo.description} />
      <link rel="canonical" href={canonical} />

      {/* hreflang alternates */}
      <link rel="alternate" hrefLang="sk" href={`${SITE}/`} />
      <link rel="alternate" hrefLang="en" href={`${SITE}/en`} />
      <link rel="alternate" hrefLang="x-default" href={`${SITE}/`} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={ogLocaleAlt} />
      <meta property="og:site_name" content={t.site?.name ?? t.brand.name} />
      <meta property="og:title" content={t.seo.title} />
      <meta property="og:description" content={t.seo.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={HERO_OG} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={t.hero.subtitle} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={t.seo.title} />
      <meta name="twitter:description" content={t.seo.description} />
      <meta name="twitter:image" content={HERO_OG} />

      {/* Language + keywords */}
      <meta name="language" content={isEN ? "English" : "Slovak"} />
      <meta name="keywords" content={t.seo.keywords} />

      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(buildLodgingSchema(t, lang))}</script>
      <script type="application/ld+json">{JSON.stringify(buildWebSiteSchema(t, lang))}</script>
      <script type="application/ld+json">{JSON.stringify(buildNearbySchema(t, lang))}</script>
      <script type="application/ld+json">{JSON.stringify(buildFaqSchema(t.faq.items))}</script>
      <script type="application/ld+json">{JSON.stringify(buildBreadcrumbSchema(breadcrumbs))}</script>
    </Helmet>
  );
};
